import * as React from 'react';

import { fromJson } from '@bufbuild/protobuf';
import {
  InvoiceEvent,
  InvoiceEventSchema,
} from '@rematter/frontend-lib-api-client/grpc/finance/v1/invoice_events_pb';
import { Col, EventDetailContainer, JsonViewer } from '@rematter/ui-web';

import { InvoiceCreatedDetail } from './InvoiceCreatedDetail';
import { InvoiceDeletedDetail } from './InvoiceDeletedDetail';
import { InvoicePaidDetail } from './InvoicePaidDetail';
import { InvoiceUpdatedDetail } from './InvoiceUpdatedDetail';
import { InvoiceVoidedDetail } from './InvoiceVoidedDetail';

import { useTranslations } from '@/finance/i18n/useTranslations';

type InvoiceEventDetailProps = {
  payload: string;
};

export const InvoiceEventDetail = ({ payload }: InvoiceEventDetailProps) => {
  const t = useTranslations();
  const [showJson, setShowJson] = React.useState(false);

  const event = React.useMemo((): InvoiceEvent | null => {
    try {
      return fromJson(InvoiceEventSchema, JSON.parse(payload));
    } catch {
      return null;
    }
  }, [payload]);

  if (!event) {
    return <JsonViewer data={payload} title={t.logs.rawPayloadParseError} />;
  }

  const { event: inner } = event;

  const renderEventContent = () => {
    switch (inner.case) {
      case 'created':
        return <InvoiceCreatedDetail event={inner.value} />;
      case 'updated':
        return <InvoiceUpdatedDetail event={inner.value} />;
      case 'deleted':
        return <InvoiceDeletedDetail event={inner.value} />;
      case 'voided':
        return <InvoiceVoidedDetail event={inner.value} />;
      case 'paid':
        return <InvoicePaidDetail event={inner.value} />;
      case undefined:
        return <JsonViewer data={payload} title={t.logs.unknownEventType} />;
    }
  };

  const getEventTitle = () => {
    switch (inner.case) {
      case 'created':
        return t.logs.events.invoiceCreated;
      case 'updated':
        return t.logs.events.invoiceUpdated;
      case 'deleted':
        return t.logs.events.invoiceDeleted;
      case 'voided':
        return t.logs.events.invoiceVoided;
      case 'paid':
        return t.logs.events.invoicePaid;
      case undefined:
        return t.logs.unknownEvent;
    }
  };

  return (
    <Col className="@InvoiceEventDetail flex-1">
      <EventDetailContainer
        title={getEventTitle()}
        showJson={showJson}
        onToggleJson={() => setShowJson(!showJson)}
        jsonContent={payload}
      >
        {renderEventContent()}
      </EventDetailContainer>
    </Col>
  );
};
